import { useMutation, useQueryClient } from "@tanstack/react-query";

import { createMutationHook } from "~/shared/api/query-factories";

import {
    archiveChat,
    deleteChat,
    moveChat,
    pinChat,
    renameChat,
    unarchiveChat,
    unpinChat,
    type MoveChatPayload,
} from "./api";
import type { Chat } from "./types";
import { chatDetailKey } from "./useActiveChat";
import { chatListKey } from "./useChats";

// Chat registry list cache key (mirrors the literal used across chat features).
const CHAT_REGISTRY_KEY = ["v2", "chats", "registry"];

export interface ChatStatusVars {
    chatId: string;
    workspaceId: string;
}

/**
 * Factory for a chat status-mutation hook (archive / unarchive / pin / unpin).
 *
 * Each of these mutations behaves identically: it flips a flag on the chat,
 * writes the fresh entity into the detail cache, and invalidates the workspace
 * chat list + the registry. The only difference is the API call, so the hook is
 * derived from `createMutationHook` with the call injected.
 */
export const createChatStatusMutation = (apiFn: (chatId: string) => Promise<Chat>) =>
    createMutationHook<Chat, ChatStatusVars>({
        mutationFn: ({ chatId }) => apiFn(chatId),
        onSuccess: (chat, _vars, qc) => qc.setQueryData<Chat>(chatDetailKey(chat.id), chat),
        invalidate: (_chat, vars) => [chatListKey(vars.workspaceId), CHAT_REGISTRY_KEY],
    });

export const useArchiveChat = createChatStatusMutation(archiveChat);
export const useUnarchiveChat = createChatStatusMutation(unarchiveChat);
export const usePinChat = createChatStatusMutation(pinChat);
export const useUnpinChat = createChatStatusMutation(unpinChat);

interface DeleteChatVars {
    chatId: string;
    workspaceId: string;
}

export function useDeleteChat() {
    const qc = useQueryClient();
    return useMutation<Chat, Error, DeleteChatVars>({
        mutationFn: ({ chatId }) => deleteChat(chatId),
        onSuccess: (_chat, vars) => {
            void qc.invalidateQueries({ queryKey: chatListKey(vars.workspaceId) });
            void qc.invalidateQueries({ queryKey: CHAT_REGISTRY_KEY });
        },
    });
}

interface MoveChatVars extends MoveChatPayload {
    chatId: string;
    // Source workspace — used to invalidate the right chat list(s).
    workspaceId: string;
}

export function useMoveChat() {
    const qc = useQueryClient();
    return useMutation<Chat, Error, MoveChatVars>({
        mutationFn: ({ chatId, targetWorkspaceId, targetProjectId, targetSceneId }) =>
            moveChat(chatId, { targetWorkspaceId, targetProjectId, targetSceneId }),
        onSuccess: (chat, vars) => {
            qc.setQueryData<Chat>(chatDetailKey(chat.id), chat);
            void qc.invalidateQueries({ queryKey: chatListKey(vars.workspaceId) });
            if (chat.workspaceId !== vars.workspaceId) {
                void qc.invalidateQueries({ queryKey: chatListKey(chat.workspaceId) });
            }
            void qc.invalidateQueries({ queryKey: CHAT_REGISTRY_KEY });
        },
    });
}

interface RenameChatVars {
    chatId: string;
    title: string;
    workspaceId?: string;
}

export function useRenameChat() {
    const qc = useQueryClient();
    return useMutation<Chat, Error, RenameChatVars>({
        mutationFn: ({ chatId, title }) => renameChat(chatId, title.trim()),
        onSuccess: (updated, vars) => {
            qc.setQueryData<Chat>(chatDetailKey(vars.chatId), updated);
            void qc.invalidateQueries({ queryKey: chatListKey(vars.workspaceId) });
        },
    });
}
